/* -------------------------------------------------------------------------- */
/*                                Dependencies                                */
/* -------------------------------------------------------------------------- */

// Mantine
import { useForm } from '@mantine/form';

// Components
import {
	Button,
	Text,
	TextInput,
	Title,
} from '@components/v2/ui';

// Fetch data
import { useForgotPassword } from '@features/auth/api/forgot-password';

// I18n
import { useTranslation } from 'react-i18next';

// Configs
import { REGEX_EMAIL } from '@config/constants/regex';

// Utils
import { useCountdown } from '@hooks/useCountDown';
import {
	capitalizeString,
	formatTime,
} from '@utils/functions';

// Store
import { DefaultModal } from '@components/molecules';
import { useBreakpoint } from '@hooks/useBreakpoint';
import { Center } from '@mantine/core';
import useStore from '@store/index';
import { FormWrapper } from '../../../../components/v2/ui/public/FormWrapper';

/* -------------------------------------------------------------------------- */
/*                                  Component                                 */
/* -------------------------------------------------------------------------- */
export const ForgotPassword = (props: {
	handlenavigate: (_target: string) => void;
}) => {
	/* ******************************** Media Query ******************************* */

	const { t } = useTranslation();
	const { isMobile } = useBreakpoint();
	const {
		isShowModalEmailSent,
		setIsShowModalEmailSent,
	} = useStore();

	const form = useForm({
		initialValues: {
			email: '',
		},
		validate: {
			email: (value) =>
				REGEX_EMAIL.test(value)
					? null
					: t('form.error.invalidEmail'),
		},
	});

	const { timeLeft, startCountdown } =
		useCountdown(30000);

	const forgotPassword = useForgotPassword({
		mutationConfig: {
			onSuccess() {
				setIsShowModalEmailSent(true);
				startCountdown();
			},
		},
	});

	/* ******************************** HELPERS ******************************* */
	const handlereturn = () => {
		props.handlenavigate('signin');
	};

	/* ******************************** CONSTANTS ******************************* */
	const hasTimeLeft = timeLeft !== null;

	/* ******************************** RENDERING ******************************* */
	return (
		<form
			onSubmit={form.onSubmit((values) => {
				forgotPassword.mutate(values);
			})}
		>
			<DefaultModal
				type="information"
				opened={isShowModalEmailSent}
				onConfirm={() => {}}
				onClose={() => setIsShowModalEmailSent(false)}
				title={t('forgotPassword.emailSentMessage')}
			/>
			<FormWrapper>
				<Title
					ta="center"
					c={'var(--mantine-color-igluPrimary-9)'}
					order={isMobile ? 6 : 5}
				>
					{t('forgotPassword.title')}
				</Title>
				<Text
					ta="center"
					c={'var(--mantine-color-igluTextSecondary-9)'}
					variant={isMobile ? 'body2' : 'body1'}
				>
					{t('forgotPassword.recoverPassword')}
				</Text>
				<TextInput
					autoComplete="new-password"
					mt={'24px'}
					size="md"
					label="Email"
					autoFocus
					disabled={hasTimeLeft}
					placeholder={t('auth.yourEmailAddress')}
					{...form.getInputProps('email')}
				/>
				{hasTimeLeft && (
					<Center>
						<Text mt={'10px'} variant="caption">
							{t('forgotPassword.timerText') +
								` ${formatTime(timeLeft)} `}
						</Text>
					</Center>
				)}
				<Button
					mt={'24px'}
					fullWidth
					variant={'primary'}
					type="submit"
					disabled={hasTimeLeft}
					loading={
						forgotPassword && forgotPassword.isLoading
					}
				>
					{capitalizeString(
						t('forgotPassword.sentResetLink')
					)}
				</Button>
				<Button
					fullWidth
					variant={'transparent'}
					onClick={handlereturn}
				>
					{capitalizeString(
						t('forgotPassword.backToLogin')
					)}
				</Button>
			</FormWrapper>
		</form>
	);
};
